import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronRight, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  isProductArchived,
  partitionProductCatalogOptions,
} from "@/lib/productCatalogOptions";
import type { ProductCatalogOption } from "@/types/ProductCatalog";

function filterProductsForSearch({
  products,
  search,
  keepProductId,
}: {
  products: ProductCatalogOption[];
  search: string;
  keepProductId?: string;
}): ProductCatalogOption[] {
  const q = search.trim().toLowerCase();
  if (!q) return products;
  return products.filter((p) => {
    if (keepProductId && p._id === keepProductId) return true;
    const label = p.name?.trim() || "Untitled";
    return label.toLowerCase().includes(q);
  });
}

interface ProductCatalogPickerProps {
  value: string;
  products: ProductCatalogOption[];
  onSelect: (productId: string) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function ProductCatalogPicker({
  value,
  products,
  onSelect,
  id,
  disabled,
  placeholder = "None",
}: ProductCatalogPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedId = value.trim();
  const selectedProduct = selectedId
    ? products.find((p) => p._id === selectedId)
    : undefined;
  const selectedIsArchived = selectedProduct
    ? isProductArchived({ product: selectedProduct })
    : false;
  const [archivedOpen, setArchivedOpen] = useState(selectedIsArchived);

  useEffect(() => {
    if (selectedIsArchived) {
      setArchivedOpen(true);
    }
  }, [selectedIsArchived]);

  const filteredProducts = useMemo(
    () =>
      filterProductsForSearch({
        products,
        search: query,
        keepProductId: selectedId || undefined,
      }),
    [products, query, selectedId],
  );

  const { active, archived } = useMemo(
    () => partitionProductCatalogOptions(filteredProducts),
    [filteredProducts],
  );

  const handleSelect = useCallback(
    (productId: string) => {
      onSelect(productId);
      setOpen(false);
      setQuery("");
    },
    [onSelect],
  );

  const triggerLabel =
    selectedId && selectedProduct
      ? selectedProduct.name?.trim() || "Untitled"
    : selectedId
      ? `Unknown product (${selectedId.slice(0, 6)}…)`
    : placeholder;

  const productLabel = (product: ProductCatalogOption) =>
    product.name?.trim() || "Untitled";

  const showEmpty =
    query.trim().length > 0 && active.length === 0 && archived.length === 0;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="border-b p-2">
          <Input
            placeholder="Search products…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
              !selectedId && "bg-accent",
            )}
            onClick={() => handleSelect("")}
          >
            <Check
              className={cn(
                "h-4 w-4 shrink-0",
                !selectedId ? "opacity-100" : "opacity-0",
              )}
            />
            None
          </button>
          {showEmpty ? (
            <p className="px-2 py-2 text-sm text-muted-foreground">No products found</p>
          ) : (
            <>
              {active.map((product) => {
                const isSelected = selectedId === product._id;
                return (
                  <button
                    key={product._id}
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
                      isSelected && "bg-accent",
                    )}
                    onClick={() => handleSelect(product._id)}
                  >
                    <Check
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{productLabel(product)}</span>
                  </button>
                );
              })}
              {archived.length > 0 ? (
                <>
                  <div className="my-1 border-t" />
                  <button
                    type="button"
                    className="flex w-full items-center gap-1 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent"
                    onClick={() => setArchivedOpen((current) => !current)}
                  >
                    {archivedOpen ? (
                      <ChevronDown className="h-3 w-3 shrink-0" />
                    ) : (
                      <ChevronRight className="h-3 w-3 shrink-0" />
                    )}
                    Archived ({archived.length})
                  </button>
                  {archivedOpen
                    ? archived.map((product) => {
                        const isSelected = selectedId === product._id;
                        return (
                          <button
                            key={product._id}
                            type="button"
                            className={cn(
                              "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
                              isSelected && "bg-accent",
                            )}
                            onClick={() => handleSelect(product._id)}
                          >
                            <Check
                              className={cn(
                                "h-4 w-4 shrink-0",
                                isSelected ? "opacity-100" : "opacity-0",
                              )}
                            />
                            <span className="truncate">{productLabel(product)}</span>
                          </button>
                        );
                      })
                    : null}
                </>
              ) : null}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
